import { supabase } from '../../config/supabase';

/**
 * Get notifications for a user
 */
export const getNotifications = async (userId, showRead = false, limit = 50) => {
    try {
        let query = supabase
            .from('notifications')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .limit(limit);

        if (!showRead) {
            query = query.eq('is_read', false);
        }

        const { data: notifications, error } = await query;

        if (error) throw error;

        return notifications || [];
    } catch (error) {
        console.error('Error fetching notifications:', error);
        throw error;
    }
};

/**
 * Get unread notification count
 */
export const getUnreadNotificationCount = async (userId) => {
    try {
        const { count, error } = await supabase
            .from('notifications')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId)
            .eq('is_read', false);

        if (error) throw error;

        return count || 0;
    } catch (error) {
        console.error('Error fetching unread notification count:', error);
        return 0;
    }
};

/**
 * Mark a notification as read
 */
export const markNotificationAsRead = async (notificationId) => {
    try {
        const { error } = await supabase
            .from('notifications')
            .update({ is_read: true, read_at: new Date().toISOString() })
            .eq('id', notificationId);

        if (error) throw error;

        return { success: true };
    } catch (error) {
        console.error('Error marking notification as read:', error);
        throw error;
    }
};

/**
 * Mark all notifications as read for a user
 */
export const markAllNotificationsAsRead = async (userId) => {
    try {
        const { error } = await supabase
            .from('notifications')
            .update({ is_read: true, read_at: new Date().toISOString() })
            .eq('user_id', userId)
            .eq('is_read', false);

        if (error) throw error;

        return { success: true };
    } catch (error) {
        console.error('Error marking all notifications as read:', error);
        throw error;
    }
};

/**
 * Delete a notification
 */
export const deleteNotification = async (notificationId) => {
    try {
        const { error } = await supabase
            .from('notifications')
            .delete()
            .eq('id', notificationId);

        if (error) throw error;

        return { success: true };
    } catch (error) {
        console.error('Error deleting notification:', error);
        throw error;
    }
};

/**
 * Create a notification
 */
export const createNotification = async (notificationData) => {
    try {
        const { data: notification, error } = await supabase
            .from('notifications')
            .insert({
                user_id: notificationData.userId,
                company_id: notificationData.companyId,
                type: notificationData.type,
                title: notificationData.title,
                message: notificationData.message,
                priority: notificationData.priority || 'normal',
                related_type: notificationData.relatedType || null,
                related_id: notificationData.relatedId || null,
                action_url: notificationData.actionUrl || null,
                metadata: notificationData.metadata || null
            })
            .select()
            .single();

        if (error) throw error;

        return notification;
    } catch (error) {
        console.error('Error creating notification:', error);
        throw error;
    }
};

/**
 * Subscribe to new notifications
 */
export const subscribeToNotifications = (userId, callback) => {
    const subscription = supabase
        .channel('notifications')
        .on(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: 'notifications',
                filter: `user_id=eq.${userId}`
            },
            (payload) => {
                callback(payload.new);
            }
        )
        .subscribe();

    return subscription;
};

/**
 * Notification Types
 */
export const NOTIFICATION_TYPES = {
    ASSIGNMENT_CREATED: 'assignment_created',
    ASSIGNMENT_UPDATED: 'assignment_updated',
    ASSIGNMENT_COMPLETED: 'assignment_completed',
    ASSIGNMENT_DUE_SOON: 'assignment_due_soon',
    ASSIGNMENT_ASSIGNED: 'assignment_assigned',
    MESSAGE_RECEIVED: 'message_received',
    USER_MENTIONED: 'user_mentioned',
    SYSTEM_ALERT: 'system_alert',
    SYSTEM_UPDATE: 'system_update',
    PAYMENT_SUCCESS: 'payment_success',
    PAYMENT_FAILED: 'payment_failed'
};

/**
 * Notification Priorities
 */
export const NOTIFICATION_PRIORITIES = {
    LOW: 'low',
    NORMAL: 'normal',
    HIGH: 'high',
    URGENT: 'urgent'
};
